import React, { createContext, useContext, useState, useEffect, useCallback } from "react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const AuthContext = createContext(null);

// authError value for "the backend never gave us a usable answer". Distinct from
// being signed out: the token is still in localStorage and may be perfectly good.
export const AUTH_UNREACHABLE = "unreachable";

export const useAuth = () => useContext(AuthContext);

// The deliberate single-session kick: a 401 whose detail names another device.
const isSessionKick = (err) => {
  const detail = err?.response?.data?.detail;
  return err?.response?.status === 401
    && typeof detail === "string"
    && detail.toLowerCase().includes("another device");
};

// Exported for tests. What a failed bootstrap /auth/me actually means:
//   "kicked"      — single-session logout; the api() interceptor owns the cleanup.
//   "invalid"     — the server actively rejected the credentials (401/403).
//   "unreachable" — network error, timeout, CORS, or a 5xx. Says nothing about
//                   whether the token is good, so it must not sign anyone out.
export const classifyBootstrapFailure = (err) => {
  if (isSessionKick(err)) return "kicked";
  const status = err?.response?.status;
  if (status === 401 || status === 403) return "invalid";
  return "unreachable";
};

// Exported for tests. Applies the consequences of a failed bootstrap /auth/me.
// Returns the classification so callers (and tests) can see which branch ran.
export const applyBootstrapFailure = (err, { setToken, setUser, setAuthError, setLoading }) => {
  const outcome = classifyBootstrapFailure(err);
  if (outcome === "invalid") {
    localStorage.removeItem("ysv_token");
    setToken(null);
    setUser(null);
    setAuthError(null);
  } else if (outcome === "unreachable") {
    // Keep the token. A backend restart or a dropped connection used to log the
    // whole user base out here; now it surfaces a retryable error instead.
    setAuthError(AUTH_UNREACHABLE);
  }
  // "kicked" needs nothing: the interceptor already cleared the token, raised
  // the banner, and is redirecting to /login. Touching state again would only
  // race that path.
  setLoading(false);
  return outcome;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem("ysv_token"));
  const [loading, setLoading] = useState(true);
  const [authError, setAuthError] = useState(null);
  const [retryNonce, setRetryNonce] = useState(0);
  const [sessionKickMessage, setSessionKickMessage] = useState(null);

  const api = useCallback(() => {
    const t = localStorage.getItem("ysv_token");
    const instance = axios.create({
      baseURL: API,
      headers: t ? { Authorization: `Bearer ${t}` } : {},
    });
    // Detect 401s signalling another device login — force logout + surface a banner.
    instance.interceptors.response.use(
      (res) => res,
      (err) => {
        if (isSessionKick(err)) {
          localStorage.removeItem("ysv_token");
          setToken(null);
          setUser(null);
          setSessionKickMessage(err.response.data.detail);
          setTimeout(() => { window.location.href = "/login"; }, 1200);
        }
        return Promise.reject(err);
      }
    );
    return instance;
  }, []);

  useEffect(() => {
    if (!token) {
      setLoading(false);
      return undefined;
    }
    let cancelled = false;
    api().get("/auth/me").then(r => {
      if (cancelled) return;
      setUser(r.data);
      setAuthError(null);
      setLoading(false);
    }).catch(err => {
      if (cancelled) return;
      applyBootstrapFailure(err, { setToken, setUser, setAuthError, setLoading });
    });
    return () => { cancelled = true; };
  }, [token, api, retryNonce]);

  // Re-run the bootstrap after an AUTH_UNREACHABLE failure, e.g. from the
  // "couldn't reach the server" screen's retry button.
  const retryAuth = useCallback(() => {
    setAuthError(null);
    setLoading(true);
    setRetryNonce(n => n + 1);
  }, []);

  const login = async (email, password) => {
    const r = await api().post("/auth/login", { email, password });
    localStorage.setItem("ysv_token", r.data.token);
    setToken(r.data.token);
    setUser(r.data.user);
    return r.data;
  };

  const register = async (email, password, name, role) => {
    const r = await api().post("/auth/register", { email, password, name, role });
    localStorage.setItem("ysv_token", r.data.token);
    setToken(r.data.token);
    setUser(r.data.user);
    return r.data;
  };

  const logout = async () => {
    const currentToken = localStorage.getItem("ysv_token");
    if (currentToken) {
      try {
        await axios.post(`${API}/auth/logout`, {}, {
          headers: { Authorization: `Bearer ${currentToken}` },
        });
      } catch {
        // Local logout must still succeed if the API is temporarily unavailable.
      }
    }
    localStorage.removeItem("ysv_token");
    setToken(null);
    setUser(null);
    setAuthError(null);
  };

  // For login flows that bypass /auth/login (GDO phone login, parent claim auto-login,
  // etc.). The caller already has a valid token + user payload from a custom endpoint
  // and just needs the AuthContext state to mirror that so SPA navigation works without
  // a hard refresh. Without this, useAuth().user stays null and SubscriptionRoute
  // / ProtectedRoute redirect to /login.
  const adoptSession = useCallback((newToken, newUser) => {
    if (newToken) {
      localStorage.setItem("ysv_token", newToken);
      setToken(newToken);
    }
    if (newUser) setUser(newUser);
  }, []);

  const refreshUser = useCallback(async () => {
    try {
      const r = await api().get("/auth/me");
      setUser(r.data);
      return r.data;
    } catch { return null; }
  }, [api]);

  return (
    <AuthContext.Provider value={{ user, token, loading, authError, retryAuth, login, register, logout, api, setUser, refreshUser, adoptSession }}>
      {sessionKickMessage && (
        <div data-testid="session-kick-banner" style={{
          position: 'fixed', top: 0, left: 0, right: 0, zIndex: 9999,
          background: '#f44336', color: '#fff', padding: '12px 20px',
          textAlign: 'center', fontWeight: 600, fontSize: 14,
          fontFamily: "'DM Sans', sans-serif",
        }}>{sessionKickMessage}</div>
      )}
      {children}
    </AuthContext.Provider>
  );
};
